import { Button } from "@/components/ui/button";
import { Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface CaseHeaderProps {
  onCreateCase: () => void;
  onRefresh: () => Promise<void>;
}

export function CaseHeader({ onCreateCase, onRefresh }: CaseHeaderProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 sm:p-6 bg-gradient-to-r from-amber-50/80 to-white dark:from-amber-950/20 dark:to-background rounded-lg border shadow-sm backdrop-blur-sm"
    >
      <div className="space-y-1">
        <motion.h1 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-700 to-amber-900 dark:from-amber-400 dark:to-amber-600 bg-clip-text text-transparent"
        >
          Saker
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="text-sm sm:text-base text-muted-foreground"
        >
          Administrer og følg opp dine juridiske saker
        </motion.p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={cn(
            "w-full sm:w-auto transition-all duration-200",
            "bg-white dark:bg-background hover:bg-primary/5 active:scale-95",
            "border-amber-200 dark:border-amber-900/50",
            "text-amber-900 dark:text-amber-400"
          )}
        >
          <RefreshCw className={cn(
            "mr-2 h-4 w-4",
            isRefreshing && "animate-spin"
          )} />
          Oppdater
        </Button>
        <Button 
          onClick={onCreateCase}
          className={cn(
            "w-full sm:w-auto transition-all duration-200",
            "bg-gradient-to-r from-amber-600 to-amber-700",
            "hover:from-amber-700 hover:to-amber-800",
            "text-white active:scale-95",
            "border-none",
            "whitespace-nowrap" // Prevent text wrapping
          )}
        >
          <Plus className="mr-2 h-4 w-4" />
          Ny Sak
        </Button>
      </div>
    </motion.div>
  );
}